import React, { useCallback, useContext, useEffect, useState } from "react";
import { createUseStyles } from "react-jss";
import PropTypes from "prop-types";
import _ from "lodash";
import jssVariables from "../../../../jss_setup";
import {
  CurrencyContext,
  TranslationContext,
  currencies,
} from "../../../../umbilical_tools";
import { DateTime } from "luxon";
import { InlineIcon } from "@iconify/react";
import { getExchangedPrice } from "../../../../umbilical_hocs";
import { PrebookLoader } from "./helpers";
import { trainPrebook } from "../../../../api/trains";
import {
  useGetTraQueryParams,
  useTrainsGetSessionMeta,
} from "../../../../hooks/trains";
import { CarrierIcon } from "../../../../pages/Trains/helpers";
import { toast } from "react-toastify";
import {
  trainSearchResultsFailed,
  trainSearchResultsIdle,
} from "../../../../actions/search/trains_search";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { CustomCheckbox } from "../../../forms/inputGroup/helpers";

export function actionFailed({ history, onFailed, errorMsg }) {
  onFailed();
  toast.warning(errorMsg, { duration: 3000 });
  history.push("/");
}

const trainDetailsstyles = createUseStyles({
  TrainDetails: (props) => ({
    display: "grid",
    gridGap: jssVariables.normal_gap,
    ..._.get(props, "customStyles.PrebookUpdate", {}),
  }),
  body: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr",
    fontSize: "medium",
    ..._.get(props, "customStyles.body", {}),
  }),
  stationInfoDate: (props) => ({
    color: "rgba(0, 0, 0, 0.4)",
    ..._.get(props, "customStyles.stationInfoDate", {}),
  }),
  services: (props) => ({
    color: "rgba(0, 0, 0, 0.4)",
    ..._.get(props, "customStyles.services", {}),
  }),
  header: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr max-content",
    paddingTop: jssVariables.half_gap,
    color: jssVariables.colors.base_color,
    fontSize: "large",
    ..._.get(props, "customStyles.header", {}),
  }),
  stationInfo: (props) => ({
    display: "grid",
    alignItems: "center",
    position: "relative",
    width: "95%",
    fontWeight: "500",
    paddingLeft: jssVariables.double_gap,
    "&:before": {
      content: `''`,
      position: "absolute",
      left: 0,
      top: ".2rem",
      height: `calc(${jssVariables.normal_gap} * 1.3)`,
      width: `calc(${jssVariables.normal_gap} * 1.3)`,
      borderRadius: "50%",
      border: `3px solid ${jssVariables.colors.base_color}`,
    },
    "&:not(:last-child):after": {
      content: `''`,
      position: "absolute",
      left: ".4rem",
      top: `calc(${jssVariables.normal_gap} * 2)`,
      height: "calc(100% - 1.6rem)",
      borderLeft: `3px dashed ${jssVariables.colors.base_color}`,
    },
    ..._.get(props, "customStyles.stationInfo", {}),
  }),
  divider: (props) => ({
    width: "100%",
    height: "1px",
    background: jssVariables.colors.border,
    margin: `calc(${jssVariables.normal_gap} * 1) 0`,
    ..._.get(props, "customStyles.divider", {}),
  }),
  conditionsContainer: (props) => ({
    width: "75vw",
    display: "grid",
    gridTemplateColumns: "1fr max-content",
    placeItems: "center",
    placeContent: "space-between",
    overflowX: "hidden",
    overflowY: "auto",
    gridGap: jssVariables.normal_gap,
    padding: jssVariables.half_gap,
    border: `1px solid ${jssVariables.colors.border}`,
    position: "relative",
    boxSizing: "border-box",
    ..._.get(props, "customStyles.conditionsContainer", {}),
  }),
  conditionsTitle: (props) => ({
    placeSelf: "start",
    fontWeight: "500",
    fontSize: "large",
    padding: [0, jssVariables.normal_gap],
    color: jssVariables.colors.base_color,
    ..._.get(props, "customStyles.conditionsTitle", {}),
  }),
  conditions: (props) => ({
    fontWeight: "400",
    fontSize: "medium",
    padding: [0, jssVariables.normal_gap],
    boxSizing: "border-box",
    wordWrap: "break-word",
    ..._.get(props, "customStyles.conditions", {}),
  }),
  iconContainer: (props) => ({
    paddingRight: jssVariables.half_gap,
    ..._.get(props, "customStyles.iconContainer", {}),
  }),
  arrowIcon: (props) => ({
    display: "grid",
    placeItems: "center",
    color: "white",
    fontSize: "large",
    width: jssVariables.double_gap,
    height: jssVariables.double_gap,
    borderRadius: "30%",
    background: jssVariables.colors.base_color,
    cursor: "pointer",
    ..._.get(props, "customStyles.arrowIcon", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    conditionsContainer: (props) => ({
      width: "40vw",
      ..._.get(props, "customStyles.conditionsContainer.smallscreen", {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {
    conditionsContainer: (props) => ({
      width: "27vw",
      ..._.get(props, "customStyles.conditionsContainer.bigscreen", {}),
    }),
  },
});
export const TrainDetails = ({ segment, idx, segmentsLength }) => {
  const classes = trainDetailsstyles({});
  const { t } = useContext(TranslationContext);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setExpanded(false);
  }, []);

  return (
    <div className={classes.TrainDetails}>
      <div className={classes.header}>
        {segment.vehicle.type} #{segment.vehicle.reference} -{" "}
        {segment.product.comfort_category.label} ,{" "}
        {segment.product.flexibility.label}
        <CarrierIcon carrier={segment.marketingCarrier} />
      </div>
      <div className={classes.body}>
        <div className={classes.stationInfo}>
          {DateTime.fromISO(segment.departure).toLocal().toFormat("hh:mm")}{" "}
          {segment.origin.label}
          <div>
            <div className={classes.stationInfoDate}>
              {DateTime.fromISO(segment.departure).toLocaleString(
                DateTime.DATE_MED_WITH_WEEKDAY
              )}
            </div>
            <div className={classes.services}>
              {segment.services
                ? segment.services.map((service) => service.label).join(", ")
                : t("noServices")}
            </div>
          </div>
        </div>
        <div className={classes.stationInfo}>
          {DateTime.fromISO(segment.arrival).toLocal().toFormat("hh:mm")}{" "}
          {segment.destination.label}
        </div>
      </div>
      {idx + 1 === segmentsLength && (
        <div className={classes.conditionsContainer}>
          <div className={classes.conditionsTitle}>{t("fareConditions")}</div>
          <div className={classes.iconContainer}>
            <InlineIcon
              icon={`material-symbols:keyboard-arrow-${
                expanded ? "up" : "down"
              }`}
              onClick={() => setExpanded(!expanded)}
              className={classes.arrowIcon}
            />
          </div>
          {expanded && (
            <div
              className={classes.conditions}
              dangerouslySetInnerHTML={{
                __html: segment.product.conditions,
              }}></div>
          )}
        </div>
      )}
      {idx + 1 !== segmentsLength && <div className={classes.divider} />}
    </div>
  );
};
TrainDetails.defaultProps = { segment: {}, idx: 0, segmentsLength: 0 };
TrainDetails.propTypes = {
  segment: PropTypes.object.isRequired,
  idx: PropTypes.number.isRequired,
  segmentsLength: PropTypes.number.isRequired,
};

const styles = createUseStyles({
  Prebook: (props) => ({
    display: "grid",
    gridGap: jssVariables.normal_gap,
    maxHeight: "65vh",
    ..._.get(props, "customStyles.PrebookUpdate", {}),
  }),
  mainContainer: (props) => ({
    boxShadow: jssVariables.shadows.normalShadow,
    padding: jssVariables.double_gap,
    borderRadius: jssVariables.half_gap,
    overflowY: "auto",
    maxHeight: "66vh",
    ..._.get(props, "customStyles.mainContainer", {}),
  }),
  feeContainer: (props) => ({
    display: "grid",
    gridGap: jssVariables.normal_gap,
    height: "max-content",
    padding: jssVariables.normal_gap,
    boxShadow: jssVariables.shadows.normalShadow,
    borderRadius: jssVariables.half_gap,
    ..._.get(props, "customStyles.feeContainer", {}),
  }),
  fee: (props) => ({
    display: "grid",
    gridGap: jssVariables.double_gap,
    gridTemplateColumns: "1fr max-content",
    placeContent: "space-between",
    padding: [0, jssVariables.double_gap],
    ..._.get(props, "customStyles.fee", {}),
  }),
  feeLabel: (props) => ({
    fontSize: "medium",
    fontWeight: 500,
    color: jssVariables.colors.base_color,
    ..._.get(props, "customStyles.feeLabel", {}),
  }),
  feePrice: (props) => ({
    fontSize: "medium",
    fontWeight: 500,
    color: "rgba(0,0,0,0.4)",
    ..._.get(props, "customStyles.feeValue", {}),
  }),
  header: (props) => ({
    display: "grid",
    gridTemplateColumns: "max-content 1fr",
    gridGap: jssVariables.normal_gap,
    background: jssVariables.colors.base_color,
    color: jssVariables.colors.text.light,
    fontSize: "medium",
    fontWeight: 500,
    padding: [jssVariables.normal_gap, jssVariables.normal_gap],
    borderRadius: jssVariables.half_gap,
    ..._.get(props, "customStyles.header", {}),
  }),
  trainIcon: (props) => ({
    fontSize: "x-large",
    placeSelf: "center",
    ..._.get(props, "customStyles.trainIcon", {}),
  }),
  headerTitle: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr max-content",
    placeContent: "space-between",
    ..._.get(props, "customStyles.headerTitle", {}),
  }),
  content: (props) => ({
    display: "grid",
    gridGap: jssVariables.normal_gap,
    padding: jssVariables.normal_gap,
    fontSize: "large",
    ..._.get(props, "customStyles.content", {}),
  }),
  divider: (props) => ({
    width: "100%",
    height: "2px",
    background: jssVariables.colors.base_color,
    margin: `calc(${jssVariables.normal_gap} * 1) 0`,
    ..._.get(props, "customStyles.divider", {}),
  }),
  totalPriceContainer: (props) => ({
    display: "grid",
    gridTemplateColumns: "max-content max-content",
    gridGap: jssVariables.normal_gap,
    placeContent: "end",
    fontSize: "x-large",
    ..._.get(props, "customStyles.totalPriceContainer", {}),
  }),
  total: (props) => ({
    color: "rgba(0, 0, 0, 0.4)",
    ..._.get(props, "customStyles.total", {}),
  }),
  price: (props) => ({
    fontWeight: 500,
    color: jssVariables.colors.base_color,
    ..._.get(props, "customStyles.price", {}),
  }),
  timesAndSeats: (props) => ({
    display: "none",
    ..._.get(props, "customStyles.timesAndSeats", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    mainContainer: (props) => ({
      maxHeight: "60vh",
      ..._.get(props, "customStyles.mainContainer.smallscreen", {}),
    }),
    header: (props) => ({
      fontSize: "large",
      ..._.get(props, "customStyles.header.smallscreen", {}),
    }),
    timesAndSeats: (props) => ({
      display: "grid",
      ..._.get(props, "customStyles.timesAndSeats.smallscreen", {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {
    mainContainer: (props) => ({
      maxHeight: "78vh",
      ..._.get(props, "customStyles.mainContainer.bigscreen", {}),
    }),
  },
});
export const Prebook = ({
  loading,
  setLoading,
  page_session_id,
  prebookData,
  setPrebookData,
  agreed,
  setAgreed,
}) => {
  const classes = styles({});
  const { t } = useContext(TranslationContext);
  const { currency: settedCurrency } = useContext(CurrencyContext);

  const dispatch = useDispatch();
  const history = useHistory();

  const { selectionData } = useGetTraQueryParams();
  const { search_session_id, market_type } = useTrainsGetSessionMeta({
    page_session_id,
  });

  const goRoute = _.get(prebookData, "booking_items.0.legs.0", []);
  const origin = _.get(goRoute, "origin.label", "");
  const destination = _.get(goRoute, "destination.label", "");
  const returnRoute = _.get(prebookData, "booking_items.0.legs.1", []);
  const hasReturn = !_.isEmpty(returnRoute);
  const goRouteSegments = _.get(goRoute, "segments", []);
  const returnRouteSegments = _.get(returnRoute, "segments", []);
  const totalPrice = _.get(prebookData, "prices.value_net", 0);
  const priceCurrency = _.get(prebookData, "prices.currency", "");
  const feeLabel = _.get(prebookData, "fees.label", "");
  const feeCurrency = _.get(prebookData, "fees.amount.currency", "");
  const fee = _.get(prebookData, "fees.amount.value", 0);
  const warnings = _.get(prebookData, "booking_items.0.warnings", []);
  const additionalCosts = warnings.filter((warning) =>
    ["era-bookings-price-discrepancy-seat-reservation"].includes(warning.code)
  );

  const onFailed = useCallback(() => {
    dispatch(trainSearchResultsIdle());
    dispatch(trainSearchResultsFailed());
  }, [dispatch]);

  async function onPrebook({ payload, onFailed }) {
    const results = await trainPrebook({
      payload,
      onFailed: (errorMsg) => actionFailed({ history, onFailed, errorMsg }),
    });

    if (!results) return;
    setPrebookData(results);
  }

  // Execute Prebook effect
  useEffect(() => {
    if (!search_session_id || !market_type) return;
    var prebook_items = [selectionData?.selectedType?.goRoute?.url ?? ""];
    if (selectionData?.selectedType.returnRoute) {
      prebook_items.push(selectionData?.selectedType?.returnRoute?.url ?? "");
    }
    var payload = {
      supplier: "raileurope",
      session_id: search_session_id,
      api_uid: selectionData?.selectedType?.goRoute?.api_uid ?? "",
      market_type,
      prebook_items,
    };
    onPrebook({ payload, onFailed });
  }, [search_session_id, market_type]);

  // Handle loading effect
  useEffect(() => {
    if (!_.isEmpty(prebookData)) setLoading(false);
  }, [prebookData]);

  return (
    <div className={classes.Prebook}>
      {loading ? (
        <PrebookLoader />
      ) : (
        <React.Fragment>
          <div className={classes.mainContainer}>
            <div className={classes.header}>
              <InlineIcon
                className={classes.trainIcon}
                icon="ic:baseline-train"
              />
              <span className={classes.headerTitle}>
                {origin} {t("to")} {destination}{" "}
                {hasReturn && `, ${t("return")}`}
                <span className={classes.timesAndSeats}>
                  {t("timesAndSeats")}
                </span>
              </span>
            </div>
            <div className={classes.content}>
              {goRouteSegments &&
                goRouteSegments.map((segment, idx) => (
                  <TrainDetails
                    segment={segment}
                    key={idx}
                    idx={idx}
                    segmentsLength={goRouteSegments.length}
                  />
                ))}
              {returnRouteSegments.length > 0 && (
                <React.Fragment>
                  <div className={classes.divider} />
                  {returnRouteSegments.map((segment, idx) => (
                    <TrainDetails
                      segment={segment}
                      key={idx}
                      idx={idx}
                      segmentsLength={returnRouteSegments.length}
                    />
                  ))}
                </React.Fragment>
              )}
            </div>
            <CustomCheckbox
              label={t("iHaveReadAgreeWithAbove")}
              checked={agreed}
              setChecked={setAgreed}
            />
          </div>
          <div className={classes.feeContainer}>
            <div className={classes.fee}>
              <span className={classes.feeLabel}> {feeLabel}</span>{" "}
              <span className={classes.feePrice}>
                {currencies[settedCurrency]}
                {getExchangedPrice({
                  price: fee,
                  priceCurrency: feeCurrency,
                  settedCurrency,
                  withDecimals: true,
                })}
              </span>
            </div>
            {additionalCosts.map((cost, idx) => (
              <div className={classes.fee} key={idx}>
                <span className={classes.feeLabel}> {cost.label}</span>
                <span className={classes.feePrice}>
                  {currencies[settedCurrency]}
                  {getExchangedPrice({
                    price: cost?.details?.[0] || 0,
                    priceCurrency: cost?.details?.[1] || "",
                    settedCurrency,
                    withDecimals: true,
                  })}
                </span>
              </div>
            ))}
          </div>
          <div className={classes.totalPriceContainer}>
            <span className={classes.total}>{t("total")}</span>
            <span className={classes.price}>
              {currencies[settedCurrency]}
              {getExchangedPrice({
                price: totalPrice,
                priceCurrency: priceCurrency,
                settedCurrency,
                withDecimals: true,
              })}
            </span>
          </div>
        </React.Fragment>
      )}
    </div>
  );
};
Prebook.defaultProps = {
  loading: true,
  selectionData: {},
  prebookData: {},
  page_session_id: "",
};
Prebook.propTypes = {
  loading: PropTypes.bool.isRequired,
  setLoading: PropTypes.func.isRequired,
  selectionData: PropTypes.object.isRequired,
  setPrebookData: PropTypes.func.isRequired,
  prebookData: PropTypes.object.isRequired,
  page_session_id: PropTypes.string.isRequired,
  agreed: PropTypes.bool.isRequired,
  setAgreed: PropTypes.func.isRequired,
};
